export type ContributionPostingBaselineStatus =
  | "needs_review"
  | "missing_required_allocation"
  | "needs_deposit_batch"
  | "ready_to_post"
  | "posted"
  | "sync_failed";

export interface ContributionPostingBaselineInput {
  queueStatus: string | null;
  amount: number | null;
  allocationTotal: number;
  hasAllocations: boolean;
  depositId: string | null;
  journalEntryId?: string | null;
}

export interface ContributionPostingBaselineResult {
  status: ContributionPostingBaselineStatus;
  isPostingBlocked: boolean;
  missingRequiredAllocation: boolean;
  reason: string;
}

const FAILED_STATUSES = new Set(["sync_failed", "error"]);
const REVIEW_STATUSES = new Set(["needs_review"]);

export function evaluateContributionPostingBaseline(
  input: ContributionPostingBaselineInput,
): ContributionPostingBaselineResult {
  const normalizedAmount = Number(input.amount ?? 0);
  const roundedDiff = Math.round((normalizedAmount - input.allocationTotal) * 100) / 100;
  const missingRequiredAllocation =
    normalizedAmount > 0 && (!input.hasAllocations || Math.abs(roundedDiff) > 0.009);

  if (FAILED_STATUSES.has(input.queueStatus ?? "")) {
    return {
      status: "sync_failed",
      isPostingBlocked: true,
      missingRequiredAllocation,
      reason: "This item needs attention before it can post.",
    };
  }

  if (missingRequiredAllocation) {
    return {
      status: "missing_required_allocation",
      isPostingBlocked: true,
      missingRequiredAllocation: true,
      reason:
        "Add a full fund allocation split before posting. Once allocations match the contribution total, posting will be available.",
    };
  }

  if (input.journalEntryId) {
    return {
      status: "posted",
      isPostingBlocked: false,
      missingRequiredAllocation: false,
      reason: "Posted to journal.",
    };
  }

  if (!input.depositId) {
    return {
      status: "needs_deposit_batch",
      isPostingBlocked: false,
      missingRequiredAllocation: false,
      reason: "Add this contribution to a deposit batch before posting.",
    };
  }

  if (REVIEW_STATUSES.has(input.queueStatus ?? "")) {
    return {
      status: "needs_review",
      isPostingBlocked: false,
      missingRequiredAllocation: false,
      reason: "Review details before posting.",
    };
  }

  return {
    status: "ready_to_post",
    isPostingBlocked: false,
    missingRequiredAllocation: false,
    reason: "Ready for posting baseline.",
  };
}
